import React from 'react';
import { createRoot } from 'react-dom/client';
import '../css/tailwind.css';

(function() {
  const ready = (fn) => {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  };

    const setupContactForm = () => {
    const form = document.querySelector('[data-contact-form]');
    if (!form) return;

    const statusEl = form.querySelector('[data-contact-status]');
    const submitBtn = form.querySelector('[data-contact-submit]');
    const submitLabel = form.querySelector('[data-contact-submit-label]');

    const defaultLabel =
      submitBtn?.getAttribute('data-default-label') ||
      submitLabel?.textContent ||
      'Send Message';

    const loadingLabel =
      submitBtn?.getAttribute('data-loading-label') || 'Sending…';

    let isSubmitting = false;

    const setStatus = (type, message) => {
      if (!statusEl) return;
      statusEl.textContent = message;
      statusEl.classList.remove('hidden', 'text-green-600', 'text-red-600');

      if (type === 'success') {
        statusEl.classList.add('text-green-600');
        statusEl.classList.remove('text-red-600');
      } else {
        statusEl.classList.add('text-red-600');
        statusEl.classList.remove('text-green-600');
      }
    };

    const clearStatus = () => {
      if (!statusEl) return;
      statusEl.textContent = '';
      statusEl.classList.add('hidden');
      statusEl.classList.remove('text-green-600', 'text-red-600');
    };

    const setSubmitting = (submitting) => {
      isSubmitting = submitting;

      if (submitBtn) {
        submitBtn.disabled = submitting;
        submitBtn.setAttribute('aria-disabled', submitting ? 'true' : 'false');
      }

      if (submitLabel) {
        submitLabel.textContent = submitting ? loadingLabel : defaultLabel;
      }
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (isSubmitting) return;

      const formData = new FormData(form);

      const getTrimmed = (name) => {
        const value = formData.get(name);
        return (value == null ? '' : String(value)).trim();
      };

      const firstName = getTrimmed('firstName');
      const lastName = getTrimmed('lastName');
      const email = getTrimmed('email');
      const phone = getTrimmed('phone');
      const message = getTrimmed('message');

      formData.set('firstName', firstName);
      formData.set('lastName', lastName);
      formData.set('email', email);
      formData.set('phone', phone);
      formData.set('message', message);

      if (!firstName || !lastName || !email || !message) {
        setStatus(
          'error',
          'Please fill in your first name, last name, email, and message before sending.'
        );
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setStatus('error', 'Please enter a valid email address.');
        return;
      }

      setSubmitting(true);
      clearStatus();

      try {
        const response = await fetch(
          '/wp-content/themes/jwsl-theme/contact-submit.php',
          {
            method: 'POST',
            body: formData,
          }
        );

        let data = null;
        try {
          data = await response.json();
        } catch (e) {
          data = null;
        }

        const success = response.ok && data && data.success;

        if (!success) {
          const errorMessage =
            (data && data.error) ||
            "We couldn't send your message right now. Please try again later.";
          setStatus('error', errorMessage);
          return;
        }

        form.reset();
        setStatus('success', 'Thanks! Your message has been sent.');
      } catch (error) {
        console.error(error);
        setStatus(
          'error',
          'Something went wrong while sending your message. Please try again.'
        );
      } finally {
        setSubmitting(false);
      }
    });
  };


  const setupMobileMenu = () => {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const panel = document.querySelector('[data-mobile-menu-panel]');
    if (!toggle || !panel) return;

    const openIcon = toggle.querySelector('[data-menu-icon="open"]');
    const closeIcon = toggle.querySelector('[data-menu-icon="close"]');

    const setMenuState = (isOpen) => {
      panel.classList.toggle('hidden', !isOpen);
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      document.body.classList.toggle('overflow-hidden', isOpen);

      if (openIcon && closeIcon) {
        openIcon.classList.toggle('hidden', isOpen);
        closeIcon.classList.toggle('hidden', !isOpen);
      }
    };

    const closeMenu = () => setMenuState(false);

    toggle.addEventListener('click', () => {
      const isOpen = panel.classList.contains('hidden');
      setMenuState(isOpen);
    });

    panel.querySelectorAll('[data-mobile-menu-close]').forEach((link) => {
      link.addEventListener('click', closeMenu);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    });
  };

  const setupDesktopSportsMenu = () => {
    const wrapper = document.querySelector('[data-desktop-sports]');
    const button = wrapper?.querySelector('[data-sports-button]');
    const panel = wrapper?.querySelector('[data-sports-panel]');

    if (!wrapper || !button || !panel) return;

    const showMenu = () => {
      panel.classList.remove('opacity-0', 'invisible');
      panel.classList.add('opacity-100', 'visible');
      button.setAttribute('aria-expanded', 'true');
    };

    const hideMenu = () => {
      panel.classList.add('opacity-0', 'invisible');
      panel.classList.remove('opacity-100', 'visible');
      button.setAttribute('aria-expanded', 'false');
    };

    wrapper.addEventListener('mouseenter', showMenu);
    wrapper.addEventListener('mouseleave', hideMenu);

    button.addEventListener('focus', showMenu);
    button.addEventListener('blur', hideMenu);
    panel.addEventListener('focusin', showMenu);
    panel.addEventListener('focusout', (event) => {
      if (!panel.contains(event.relatedTarget)) {
        hideMenu();
      }
    });
  };

  const setupMobileSportsMenu = () => {
    const toggle = document.querySelector('[data-mobile-sports-toggle]');
    const panel = document.querySelector('[data-mobile-sports-panel]');
    const chevron = toggle?.querySelector('[data-sports-chevron]');

    if (!toggle || !panel) return;

    const setOpenState = (isOpen) => {
      panel.classList.toggle('hidden', !isOpen);
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (chevron) {
        chevron.classList.toggle('rotate-180', isOpen);
      }
    };

    toggle.addEventListener('click', () => {
      const isOpen = panel.classList.contains('hidden');
      setOpenState(isOpen);
    });
  };

  const setupHeroCarousel = () => {
    const carousel = document.querySelector('[data-hero-carousel]');
    if (!carousel) return;

    const slides = Array.from(carousel.querySelectorAll('[data-hero-slide]'));
    const dots = Array.from(carousel.querySelectorAll('[data-hero-dot]'));
    const prevBtn = carousel.querySelector('.hero-prev');
    const nextBtn = carousel.querySelector('.hero-next');
    const intervalTime = parseInt(carousel.getAttribute('data-autoplay-interval') || '5000', 10);
    let currentIndex = 0;
    let isPaused = false;
    let intervalId = null;

    const setActiveSlide = (index) => {
      currentIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('opacity-100', i === currentIndex);
        slide.classList.toggle('opacity-0', i !== currentIndex);
        slide.classList.toggle('z-10', i === currentIndex);
        slide.classList.toggle('z-0', i !== currentIndex);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('bg-white', i === currentIndex);
        dot.classList.toggle('bg-white/50', i !== currentIndex);
        dot.setAttribute('aria-current', i === currentIndex ? 'true' : 'false');
      });
    };

    const nextSlide = () => setActiveSlide(currentIndex + 1);
    const prevSlide = () => setActiveSlide(currentIndex - 1);

    const startAutoplay = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(() => {
        if (!isPaused) {
          nextSlide();
        }
      }, intervalTime);
    };

    const pause = () => { isPaused = true; };
    const resume = () => { isPaused = false; };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => setActiveSlide(index));
    });

    prevBtn?.addEventListener('click', prevSlide);
    nextBtn?.addEventListener('click', nextSlide);

    carousel.addEventListener('mouseenter', pause);
    carousel.addEventListener('mouseleave', resume);
    carousel.addEventListener('focusin', pause);
    carousel.addEventListener('focusout', resume);

    setActiveSlide(0);
    startAutoplay();
  };

  const setupAnimatedSections = () => {
    const animated = document.querySelectorAll('[data-animated-section]');
    if (!animated.length) return;

    const animationMap = {
      'fade': {
        initial: ['opacity-0'],
        visible: ['opacity-100'],
        transition: ['transition-all', 'duration-700', 'ease-out'],
      },
      'slide-up': {
        initial: ['opacity-0', 'translate-y-8'],
        visible: ['opacity-100', 'translate-y-0'],
        transition: ['transition-all', 'duration-700', 'ease-out'],
      },
      'slide-left': {
        initial: ['opacity-0', 'translate-x-8'],
        visible: ['opacity-100', 'translate-x-0'],
        transition: ['transition-all', 'duration-700', 'ease-out'],
      },
      'slide-right': {
        initial: ['opacity-0', '-translate-x-8'],
        visible: ['opacity-100', 'translate-x-0'],
        transition: ['transition-all', 'duration-700', 'ease-out'],
      },
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const target = entry.target;
        const animation = target.getAttribute('data-animation') || 'fade';
        const delay = parseInt(target.getAttribute('data-delay') || '0', 10);
        const config = animationMap[animation] || animationMap['fade'];

        if (!target.dataset.jwslInit) {
          target.classList.add(...config.initial, ...config.transition);
          target.dataset.jwslInit = 'true';
        }

        if (entry.isIntersecting) {
          setTimeout(() => {
            target.classList.add(...config.visible);
            config.initial.forEach((cls) => target.classList.remove(cls));
          }, delay);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    animated.forEach((el) => observer.observe(el));
  };

  const setupTicker = () => {
    const ticker = document.querySelector('[data-ticker]');
    if (!ticker) return;

    const track = ticker.querySelector('[data-ticker-track]');
    if (!track) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const setAnimationState = () => {
      const shouldPause = prefersReducedMotion.matches || ticker.dataset.prefersReducedMotion === 'true';
      track.style.animationPlayState = shouldPause ? 'paused' : 'running';
    };

    track.style.animation = 'ticker-scroll 30s linear infinite';
    setAnimationState();
    prefersReducedMotion.addEventListener('change', setAnimationState);
  };

  const setupImageModal = () => {
    const modal = document.querySelector('[data-image-modal]');
    const modalImage = modal?.querySelector('[data-image-modal-target]');
    const closeButton = modal?.querySelector('[data-image-modal-close]');
    const triggers = document.querySelectorAll('[data-image-trigger]');

    if (!modal || !modalImage || !closeButton || !triggers.length) return;

    const openModal = (src, alt) => {
      modalImage.setAttribute('src', src);
      modalImage.setAttribute('alt', alt || '');
      modal.classList.remove('hidden');
      modal.setAttribute('aria-hidden', 'false');
    };

    const closeModal = () => {
      modal.classList.add('hidden');
      modal.setAttribute('aria-hidden', 'true');
      modalImage.setAttribute('src', '');
      modalImage.setAttribute('alt', '');
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const src = trigger.getAttribute('data-image-src');
        const alt = trigger.getAttribute('data-image-alt') || '';
        if (src) {
          openModal(src, alt);
        }
      });
    });

    closeButton.addEventListener('click', closeModal);
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        closeModal();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.classList.contains('hidden')) {
        closeModal();
      }
    });
  };

  ready(() => {
    setupMobileMenu();
    setupHeroCarousel();
    setupAnimatedSections();
    setupTicker();
    setupImageModal();
    setupDesktopSportsMenu();
    setupMobileSportsMenu();
    setupContactForm();
  });
})();
